import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, Home, X, Loader2, CheckCircle2, BedDouble } from 'lucide-react';

type RoomStatus = 'AVAILABLE' | 'OCCUPIED' | 'CLEANING' | 'MAINTENANCE' | 'RESERVED';

interface StatusUpdate {
  id: string;
  roomNumber: string;
  newStatus: RoomStatus;
  reason?: string;
}

const STATUS_CONFIG: Record<RoomStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
  AVAILABLE:   { label: 'Available',   color: 'text-emerald-400', bg: 'bg-emerald-500/15',  border: 'border-emerald-500/40', dot: 'bg-emerald-400' },
  RESERVED:    { label: 'Reserved',    color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/15',  border: 'border-fuchsia-500/40', dot: 'bg-fuchsia-400' },
  OCCUPIED:    { label: 'Occupied',    color: 'text-cyan-400',    bg: 'bg-cyan-500/15',     border: 'border-cyan-500/40',    dot: 'bg-cyan-400' },
  CLEANING:    { label: 'Cleaning',    color: 'text-yellow-400',  bg: 'bg-yellow-500/15',   border: 'border-yellow-500/40',  dot: 'bg-yellow-400' },
  MAINTENANCE: { label: 'Maintenance', color: 'text-red-400',     bg: 'bg-red-500/15',      border: 'border-red-500/40',     dot: 'bg-red-400' },
};

interface HotelRoom { id: number; roomNumber: string; status: RoomStatus; }

interface RoomStatusHologramProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  rooms: HotelRoom[];
  onRoomsUpdated: () => void;
}

export default function RoomStatusHologram({ isOpen, onClose, storeApiKey, rooms, onRoomsUpdated }: RoomStatusHologramProps) {
  const [updates, setUpdates] = useState<StatusUpdate[]>([]);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [status, setStatus] = useState<'listening' | 'saving' | 'saved' | 'idle'>('listening');

  // Auto-start Luna room status mode
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('luna-hotel-quick-status-start'));
    }
  }, [isOpen]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setUpdates([]);
      setJustAdded(new Set());
      setSaving(false);
      setSaved(false);
      setSavedCount(0);
      setStatus('listening');
    }
  }, [isOpen]);

  // Listen for ROOM_STATUS_UPDATE actions from Luna — accumulate into grid
  useEffect(() => {
    const handler = (e: Event) => {
      const { action, roomNumber, newStatus, reason } = (e as CustomEvent).detail || {};
      if (action !== 'ROOM_STATUS_UPDATE' || !roomNumber) return;
      const id = `${roomNumber}_${Date.now()}`;
      const upd: StatusUpdate = { id, roomNumber: String(roomNumber), newStatus: (newStatus as RoomStatus) || 'AVAILABLE', reason };
      setUpdates(prev => {
        const exists = prev.findIndex(u => u.roomNumber === String(roomNumber));
        if (exists >= 0) { const next = [...prev]; next[exists] = upd; return next; }
        return [...prev, upd];
      });
      setJustAdded(prev => { const n = new Set(prev); n.add(id); return n; });
      setTimeout(() => setJustAdded(prev => { const n = new Set(prev); n.delete(id); return n; }), 1500);
    };
    window.addEventListener('luna-hotel-action', handler);
    return () => window.removeEventListener('luna-hotel-action', handler);
  }, []);

  // Listen for luna save all
  useEffect(() => {
    const handler = async () => { await doSaveAll(); };
    window.addEventListener('luna-roomstatus-grid-save', handler);
    return () => window.removeEventListener('luna-roomstatus-grid-save', handler);
  }, [updates, storeApiKey, rooms]);

  const doSaveAll = async () => {
    if (updates.length === 0) return;
    setSaving(true);
    setStatus('saving');
    const token = localStorage.getItem('kangavena:cashierToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let count = 0;
    try {
      for (const upd of updates) {
        const room = rooms.find(r => r.roomNumber === String(upd.roomNumber));
        if (!room) continue;
        const res = await fetch(`/api/hotel/rooms/${room.id}/status`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ status: upd.newStatus, reason: upd.reason || 'Luna batch voice update' }),
        });
        if (res.ok) count++;
      }
      setSaved(true);
      setSavedCount(count);
      setStatus('saved');
      onRoomsUpdated();
      setTimeout(() => { onClose(); }, 2500);
    } catch { /* silent */ } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="room-status-hologram"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-xl flex items-end md:items-center justify-center p-0 md:p-6"
      >
        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 80, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full max-w-2xl bg-[#0d0618] border border-fuchsia-500/20 rounded-t-3xl md:rounded-3xl overflow-hidden shadow-[0_0_80px_rgba(217,70,239,0.12)]"
        >
          {/* Glow top bar */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-fuchsia-400/60 to-transparent" />

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/30 flex items-center justify-center">
                <Home size={17} className="text-fuchsia-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-fuchsia-400/60 uppercase tracking-widest">Luna AI</p>
                <p className="text-sm font-bold text-white">Batch Room Status</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 transition-all">
              <X size={14} />
            </button>
          </div>

          {/* Status strip */}
          <div className="px-5 py-3 flex items-center gap-3 bg-fuchsia-500/5 border-b border-fuchsia-500/10">
            {status === 'listening' && (
              <>
                <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-fuchsia-400" />
                <p className="text-[11px] text-fuchsia-300 font-mono">
                  Say: <span className="text-white font-bold">"101 clean, 203 occupied, 305 maintenance"</span>
                </p>
              </>
            )}
            {status === 'saving' && (
              <><Loader2 size={13} className="text-fuchsia-400 animate-spin" /><p className="text-[11px] text-fuchsia-300 font-mono">Updating rooms…</p></>
            )}
            {status === 'saved' && (
              <><CheckCircle2 size={13} className="text-emerald-400" /><p className="text-[11px] text-emerald-300 font-mono">✓ {savedCount} room{savedCount !== 1 ? 's' : ''} updated!</p></>
            )}
          </div>

          {/* Grid */}
          <div className="px-5 py-4 min-h-[180px] max-h-[380px] overflow-y-auto">
            {updates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-4">
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2, repeat: Infinity }}
                  className="w-16 h-16 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center justify-center">
                  <Mic size={28} className="text-fuchsia-400" />
                </motion.div>
                <p className="text-slate-500 text-sm text-center">
                  Speak room numbers and their statuses
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-1">
                  {(['AVAILABLE','OCCUPIED','CLEANING','MAINTENANCE'] as RoomStatus[]).map(s => (
                    <span key={s} className={`text-[10px] font-mono px-2 py-1 rounded-lg border ${STATUS_CONFIG[s].bg} ${STATUS_CONFIG[s].border} ${STATUS_CONFIG[s].color}`}>
                      {STATUS_CONFIG[s].label}
                    </span>
                  ))}
                </div>
                <p className="text-slate-600 text-[10px] font-mono text-center mt-1">
                  "Say 'room 101 clean, 203 guest in, 305 broken'"
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {updates.map(upd => {
                  const cfg = STATUS_CONFIG[upd.newStatus] || STATUS_CONFIG.AVAILABLE;
                  return (
                    <motion.div key={upd.id}
                      initial={justAdded.has(upd.id) ? { scale: 0.85, opacity: 0 } : false}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                      className={`flex flex-col gap-1.5 p-3.5 rounded-xl border transition-all ${
                        justAdded.has(upd.id)
                          ? `${cfg.bg} ${cfg.border} shadow-[0_0_14px_rgba(217,70,239,0.2)]`
                          : 'bg-white/4 border-white/8'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BedDouble size={13} className="text-slate-400" />
                          <span className="text-lg font-black text-white">{upd.roomNumber}</span>
                        </div>
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                      </div>
                      <span className={`text-[11px] font-bold ${cfg.color}`}>{cfg.label}</span>
                      {upd.reason && <span className="text-[9px] text-slate-500 truncate">{upd.reason}</span>}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          {updates.length > 0 && !saved && (
            <div className="px-5 pb-5 pt-2 border-t border-white/8 flex gap-3">
              <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-white/10 text-slate-400 text-sm font-semibold hover:bg-white/5 hover:text-white transition-all">
                Cancel
              </button>
              <motion.button whileTap={{ scale: 0.97 }}
                onClick={doSaveAll}
                disabled={saving}
                className="flex-1 h-11 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-black shadow-[0_4px_14px_rgba(217,70,239,0.3)] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Apply {updates.length} Update{updates.length !== 1 ? 's' : ''}
              </motion.button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
